<script setup>
import { computed, onBeforeUpdate } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import Breadcrumbs from "../Breadcrumbs.vue";
import Swal from "sweetalert2";
import { useRouter } from "vue-router";
import logoWhite from "@/assets/AppLogo.png"; // <-- Importa tu logo

const store = useStore();
const isRTL = computed(() => store.state.isRTL);
const isNavFixed = computed(() => store.state.isNavFixed);
const darkMode = computed(() => store.state.darkMode);

const router = useRouter();
const route = useRoute();

// Check if user is authenticated (token exists)
const isAuthenticated = computed(() => store.state.isAuthenticated); // Now using Vuex state


const navbarMinimize = () => store.commit("navbarMinimize");

const toggleNavigationOnMobile = () => {
  if (window.innerWidth < 1200) {
    navbarMinimize();
  }
};

onBeforeUpdate(() => {
  toggleNavigationOnMobile();
});

const confirmLogout = () => {
  Swal.fire({
    icon: "question",
    title: "¿Cerrar sesión?",
    text: "¿Está seguro de que desea cerrar sesión?",
    showCancelButton: true,
    confirmButtonText: "Cerrar sesión",
    cancelButtonText: "Cancelar",
    customClass: {
      confirmButton: "btn bg-gradient-success",
      cancelButton: "btn btn-dark",
    },
    buttonsStyling: false,
  }).then((result) => {
    if (result.isConfirmed) {
      logout();
    }
  });
};

const logout = async () => {
  try {
    await store.dispatch('logout'); // Let Vuex handle the cleanup
    router.push({ name: "Login" });
  } catch (error) {
    console.error("Logout failed:", error);
  }
};

// Function to handle login navigation
const goToLogin = () => {
  router.push({ name: "Login" });
};

// Function to handle register navigation
const goToRegister = () => {
  router.push({ name: "Register" }); // Make sure you have a route named "Register"
};

const showGoBackBtn = computed(() => {
  // Ajusta según el nombre o path de tu ruta de Browse.vue
  return !isAuthenticated.value && route.name !== "Explorar Cursos";
});
</script>

<template>
  <nav id="navbarBlur" :class="`${!isNavFixed
    ? 'navbar navbar-main navbar-expand-lg p-0 shadow-none position-sticky left-auto top-0 z-index-sticky'
    : `navbar navbar-main navbar-expand-lg px-0 shadow-none position-sticky left-auto top-2 z-index-sticky`
    } ${isRTL ? 'top-0 position-sticky z-index-sticky' : ''}`" v-bind="$attrs" data-scroll="true">
    <div class="px-4 pe-4 pe-lg-5 py-2 py-lg-2 w-100 d-flex align-items-center justify-content-between">
      <!-- Botón "Ir atrás" solo si NO está autenticado -->
      <div v-if="showGoBackBtn" class="go-back-btn-wrapper-navbar">
        <button class="go-back-btn-navbar" @click="$router.push('/')">
          <i class="fas fa-arrow-left"></i>
          <span class="go-back-text-navbar">Ir atrás</span>
        </button>
      </div>

      <div class="d-flex align-items-center justify-content-between w-100">
        <!-- Logo/Toggler -->
        <div class="sidenav-toggler sidenav-toggler-inner d-xl-block" :class="isRTL ? 'me-3' : ''">
          <template v-if="!isAuthenticated && !showGoBackBtn">
            <img :src="logoWhite" alt="Logo" style="height:36px; width:auto; cursor:default;" />
          </template>
          <template v-else>
            <a href="#" class="p-0 nav-link text-body" @click.prevent="navbarMinimize">
              <div class="sidenav-toggler-inner">
                <i class="sidenav-toggler-line"
                  :class="isNavFixed && !darkMode ? ' opacity-8 bg-dark' : 'bg-light'"></i>
                <i class="sidenav-toggler-line"
                  :class="isNavFixed && !darkMode ? ' opacity-8 bg-dark' : 'bg-light'"></i>
                <i class="sidenav-toggler-line"
                  :class="isNavFixed && !darkMode ? ' opacity-8 bg-dark' : 'bg-light'"></i>
              </div>
            </a>
          </template>
        </div>

        <!-- Buttons Container - Moved outside of #navbar -->
        <div class="d-flex align-items-center">
          <!-- Show Register button only when NOT authenticated -->
          <div v-if="!isAuthenticated" class="nav-item d-flex align-items-center ms-2 ms-sm-3">
            <button class="btn btn-udemy-register d-flex align-items-center px-4 px-sm-3 px-md-4 py-2 py-sm-2 fw-bold mb-0"
              @click="goToRegister">
              <i class="fas fa-user-plus me-1 me-sm-2"></i>
              <span class="d-none d-sm-inline">Regístrate</span>
            </button>
          </div>

          <!-- Show Login/Logout button -->
          <div class="nav-item d-flex align-items-center ms-2">
            <button class="btn btn-udemy-login d-flex align-items-center px-4 px-sm-3 px-md-4 py-2 py-sm-2 fw-bold mb-0"
              @click="isAuthenticated ? confirmLogout() : goToLogin()">
              <i class="fa me-1 me-sm-2" :class="isAuthenticated ? 'fa-sign-out-alt' : 'fa-sign-in-alt'"></i>
              <span class="d-none d-sm-inline">{{ isAuthenticated ? 'Cerrar Sesión' : 'Iniciar Sesión' }}</span>
            </button>
          </div>
        </div>
      </div>

      <breadcrumbs :current-page="currentRouteName" />

      <div id="navbar" class="mt-2 collapse navbar-collapse mt-sm-0 me-md-0 me-sm-4"
        :class="isRTL ? 'px-0' : 'me-sm-4'">
        <div class="pe-md-3 d-flex align-items-center" :class="isRTL ? 'me-md-auto' : 'ms-md-auto'">
        </div>
      </div>
    </div>
  </nav>
</template>

<style>
.z-index-sticky {
  z-index: 920 !important;
}

.navbar-nav {
  margin-top: 10px;
}

.navbar-main {
  /* border-top: 10px solid #16346d81; */
  border-bottom: 2px solid #2152ff;
  background-color: #f1f1f1ba;
}

.nav-hover:hover {
  scale: 1.03;
}

/* --- Udemy-style Auth Buttons --- */
.btn.btn-udemy-register {
  background: #ffffff;
  /* background: linear-gradient(332deg, #2152ff 0%, #21d4fd 100%); */
  color: #2152ff;
  border-color: #0c044b;
  border-radius: 6px;
  /* border: none; */
  box-shadow: 0 2px 8px rgb(213, 205, 205);
  transition: background 0.2s, box-shadow 0.2s;
}

.btn.btn-udemy-register:hover,
.btn-udemy-register:focus {
  background: #ebedf0;
  color: #2152ff;
  box-shadow: 0 4px 16px rgba(164, 53, 240, 0.16);
}

.btn.btn-udemy-login {
  /* background: #fff; */
  background: linear-gradient(332deg, #2152ff 0%, #21d4fd 100%);
  color: #ffffff;
  border: 1.5px solid #2d2f31;
  border-radius: 6px;
  transition: background 0.2s, color 0.2s, border 0.2s;
}

.btn-udemy-login:hover,
.btn-udemy-login:focus {
  background: linear-gradient(332deg, #2151ff65 0%, #21d4fd 100%);
  color: #ffffff;
  border-color: #0c044b;
  backdrop-filter: blur(10px);
}

.go-back-btn-wrapper-navbar {
  position: absolute;
  left: 18px;
  z-index: 1100;
}

.go-back-btn-navbar {
  background: #fff;
  border: none;
  color: #2152ff;
  font-size: 1.1rem;
  font-weight: 500;
  border-radius: 2rem;
  height: 40px;
  min-height: 40px;
  max-height: 40px;
  padding: 0 0.9rem;
  box-shadow: 0 2px 12px rgba(33, 82, 255, 0.08);
  display: flex;
  align-items: center;
  transition: background 0.18s, box-shadow 0.18s, width 0.3s, padding 0.3s;
  cursor: pointer;
  overflow: hidden;
  width: 44px;
  min-width: 44px;
  position: relative;
}

.go-back-btn-navbar .go-back-text-navbar {
  opacity: 0;
  width: 0;
  margin-left: 0;
  white-space: nowrap;
  transition: opacity 0.2s, width 0.3s, margin-left 0.3s;
  pointer-events: none;
  height: 100%;
  display: flex;
  align-items: center;
}

.go-back-btn-navbar:hover {
  background: #f5f7fa;
  box-shadow: 0 4px 24px rgba(33, 82, 255, 0.13);
  color: #1436a4;
  width: 120px;
  padding-right: 1.1rem;
}

.go-back-btn-navbar:hover .go-back-text-navbar {
  opacity: 1;
  width: auto;
  margin-left: 8px;
  pointer-events: auto;
}

.go-back-btn-navbar i {
  font-size: 1.2rem;
  transition: color 0.18s;
  height: 100%;
  display: flex;
  align-items: center;
}

@media (max-width: 400px) {
  .go-back-btn-navbar {
    width: 44px;
    min-width: 44px;
    max-width: 90px;
    font-size: 1rem;
  }

  .go-back-btn-navbar:hover {
    width: 120px;
  }
}

.sidenav-toggler-line.bg-light {
  background-color: #1b95b1 !important;
}
</style>